import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  CircleRating,
  EditIcon,
  InfoIcon,
  Tag,
  Text,
  Tooltip,
} from '@mercai/clever'
import { TagMoscow } from '@/pages/profile/components'

import { ProfileTaxStatusEnum } from '@/types'
import { ProfileInfosHeaderProps } from './infos-header.type'
import styles from './infos-header.module.css'
import {
  checkCompletedAcquisitionData,
  checkCompletedFinanceData,
} from './infos-header.constants'

export const ProfileInfosHeader = ({
  profile,
  onClickEdit,
}: ProfileInfosHeaderProps) => {
  const { t } = useTranslation('screenProfileShow')
  const { t: tEnums } = useTranslation('enums')

  const statusObject: Record<ProfileTaxStatusEnum, ReactNode> = {
    completed: (
      <Tag model="light" variant="success">
        {tEnums('profile_tax_status_completed')}
      </Tag>
    ),
    review: (
      <Tag model="light" variant="danger">
        {tEnums('profile_tax_status_review')}
      </Tag>
    ),
    pending: (
      <Tag model="light" variant="warning">
        {tEnums('profile_tax_status_pending')}
      </Tag>
    ),
    invalid: (
      <Tag model="light" variant="danger">
        {tEnums('profile_tax_status_invalid')}
      </Tag>
    ),
  }

  const acquisitionCompleted = checkCompletedAcquisitionData({
    companyAge: profile?.leads?.[0]?.companyAge,
    monthlyBuyingPotential: profile?.leads?.[0]?.monthlyBuyingPotential,
    purchaseFrequency: profile?.leads?.[0]?.purchaseFrequency,
    selectedDeliveryPeriods: profile?.selectedDeliveryPeriods,
    selectedDeliveryDays: profile?.selectedDeliveryDays,
    productOfInterest: profile?.leads?.[0]?.productOfInterest,
    industry: profile?.leads?.[0]?.industry,
    companyManufacturedProduct: profile?.leads?.[0]?.companyManufacturedProduct,
  })

  const financeCompleted = checkCompletedFinanceData({
    enabledPaymentMethods: profile?.financeProfile?.enabledPaymentMethods,
    enabledPaymentForms: profile?.financeProfile?.enabledPaymentForms,
    cfdiUsage: profile?.attributes?.cfdiUsage,
    preferredPaymentPlanType: profile?.attributes?.preferredPaymentPlanType,
  })

  return (
    <div className={styles['infos-header']}>
      <div className={styles['infos-items-box']}>
        <div>
          <Text className={styles['text-gray']} variant="title-base">
            {t('generalViewHeaderAcquisitionInfoLabel')}
          </Text>
          <CircleRating rating={acquisitionCompleted} maxRating={100} />
        </div>

        <div>
          <Text className={styles['text-gray']} variant="title-base">
            {t('generalViewHeaderFinanceInfoLabel')}
          </Text>
          <CircleRating rating={financeCompleted} maxRating={100} />
        </div>

        <div>
          <Text className={styles['text-gray']} variant="title-base">
            {t('generalViewHeaderProfileStatusLabel')}
          </Text>

          {statusObject[profile?.taxStatus || 'pending']}
        </div>

        <div>
          <Text className={styles['text-gray']} variant="title-base">
            {t('generalViewHeaderCohortLabel')}{' '}
            <Tooltip text={t('generalViewCohortDescription')} widthText="16rem">
              <InfoIcon />
            </Tooltip>
          </Text>
          <Text>{profile?.cohort}</Text>
        </div>

        <div>
          <Text className={styles['text-gray']} variant="title-base">
            {t('generalViewHeaderClientLevelLabel')}
          </Text>
          <Text>{profile?.customerLevel?.slug || ''}</Text>
        </div>

        <div>
          <Text className={styles['text-gray']} variant="title-base">
            {t('generalViewHeaderMoscowLabel')}
          </Text>
          <TagMoscow moscowClassification={profile?.moscowClassification} />
        </div>

        <div>
          <Text className={styles['text-gray']} variant="title-base">
            {t('generalViewHeaderClientStatusLabel')}
          </Text>
          <Text>{profile?.customerStatus}</Text>
        </div>

        <div>
          <Text className={styles['text-gray']} variant="title-base">
            {t('generalViewHeaderOriginTypeLabel')}
          </Text>
          <Text>
            {profile?.type ? tEnums(`profile_type_${profile?.type}`) : '-'}
          </Text>
        </div>

        <div>
          <Text className={styles['text-gray']} variant="title-base">
            {t('generalViewHeaderClientTypeLabel')}
          </Text>
          <Text>
            {profile?.customerType
              ? tEnums(`profile_customer_type_${profile?.customerType}`)
              : '-'}
          </Text>
        </div>

        <div>
          <Text className={styles['text-gray']} variant="title-base">
            {t('generalViewHeaderFraudLabel')}
          </Text>
          <Text>
            {profile?.isFraudulent
              ? t('generalViewFraudYes')
              : t('generalViewFraudNo')}
          </Text>
        </div>

        <div>
          <Text className={styles['text-gray']} variant="title-base">
            {t('generalViewHeaderTeamLabel')}
          </Text>
          <Text>
            {profile?.users && profile?.users?.length > 0
              ? profile?.users
                  ?.map((user) =>
                    tEnums(`user_team_${user?.team || 'unknown'}`),
                  )
                  ?.join(', ')
              : t('generalViewTeamNotAssigned')}
          </Text>
        </div>
      </div>

      <Button
        variant="secondary"
        className={styles.edit}
        size="small"
        onClick={onClickEdit}
      >
        <EditIcon />
      </Button>
    </div>
  )
}
